import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMonthlyReport } from "../slice/GroupDataSlice";
import { Row, Col, Form, Table, Button } from "react-bootstrap";
import { Select, MenuItem } from "@mui/material";
import {
  exportMonthlyReportPDF,
  exportMonthlyReportExcel,
} from "../pdf/MonthlyReportPdfandExcel";
import "./listings/Pages.css";

const DashboardMonthlyReport = ({ year, setYear, month, setMonth }) => {
  const dispatch = useDispatch();
  const { monthlyReport, status, error } = useSelector(
    (state) => state.groupData
  );

  useEffect(() => {
    dispatch(fetchMonthlyReport());
  }, [dispatch]);

  const years = Array.from(
    { length: new Date().getFullYear() - 2019 },
    (_, i) => 2020 + i
  );
  const monthsList = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Filter report data based on year and month
  const currentYear = new Date().getFullYear(); // e.g., 2025
  const currentMonth = new Date().getMonth() + 1; // e.g., 9 (September)
  const filteredReport = monthlyReport
    .filter((report) => {
      const matchesYear = report.year === parseInt(year);
      const matchesMonth = month ? report.month === month : true;
      return matchesYear && matchesMonth;
    })
    .sort((a, b) => a.month - b.month); // Sort by month ascending

  // Create display report, using actual data when available
  const displayReport = [];
  const monthsToShow = month
    ? [month]
    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  monthsToShow.forEach((m) => {
    const reportEntry = filteredReport.find((r) => r.month === m);
    if (reportEntry) {
      displayReport.push(reportEntry); // Use actual data if available
    } else if (
      parseInt(year) > currentYear ||
      (parseInt(year) === currentYear && m > currentMonth)
    ) {
      displayReport.push({
        year: parseInt(year),
        month: m,
        total_boxes: 0,
        active_boxes: 0,
        disconnected_boxes: 0,
        total_collection: 0,
      }); // Zero out future months
    } else {
      displayReport.push({
        year: parseInt(year),
        month: m,
        total_boxes: 0,
        active_boxes: 0,
        disconnected_boxes: 0,
        total_collection: 0,
      }); // Zero out missing past months
    }
  });

  const hasData =
    status === "succeeded" &&
    displayReport.length > 0 &&
    displayReport.some(
      (report) =>
        report.total_boxes > 0 ||
        report.active_boxes > 0 ||
        report.disconnected_boxes > 0 ||
        report.total_collection > 0
    );

  const handlePDFExport = () => {
    const isFullYear = !month;
    try {
      exportMonthlyReportPDF(displayReport, year, isFullYear);
    } catch (error) {
      console.error("PDF Export Error:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  const handleExcelExport = () => {
    const isFullYear = !month;
    try {
      exportMonthlyReportExcel(displayReport, year, isFullYear);
    } catch (error) {
      console.error("Excel Export Error:", error);
      alert("Failed to export Excel. Please try again.");
    }
  };

  return (
    <Row className="py-3">
      <Col xs="12">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="fw-bold mb-0">Monthly/Yearly Report</h2>
          {hasData && (
            <div className="d-flex gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={handlePDFExport}
                className="export-btn"
              >
                Export PDF
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleExcelExport}
                className="export-btn"
              >
                Export Excel
              </Button>
            </div>
          )}
        </div>

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group controlId="yearSelect">
                <Form.Label>Select Year</Form.Label>
                <Select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y.toString()}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="monthSelect">
                <Form.Label>Select Month (Optional)</Form.Label>
                <Select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  fullWidth
                  size="small"
                  displayEmpty
                >
                  <MenuItem value="">Full Year</MenuItem>
                  {monthsList.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        {status === "loading" && <p className="mt-3">Loading...</p>}
        {status === "failed" && (
          <p className="mt-3 text-danger">Error: {error}</p>
        )}

        {status === "succeeded" && (
          <Row className="mt-3">
            <Col>
              <h4 className="mb-3">
                Report for{" "}
                {month
                  ? `${
                      monthsList.find((m) => m.value === month)?.label
                    } ${year}`
                  : `Year ${year}`}
              </h4>
              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  className="monthly-report-table"
                >
                  <thead>
                    <tr>
                      <th className="month_and_year__heading">Month</th>
                      <th className="month_and_year__heading">Total Boxes</th>
                      <th className="month_and_year__heading">Active Boxes</th>
                      <th className="month_and_year__heading">
                        Disconnected Boxes
                      </th>
                      <th className="month_and_year__heading">
                        Total Collection
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayReport.map((report) => (
                      <tr key={report.month}>
                        <td>
                          {
                            monthsList.find((m) => m.value === report.month)
                              ?.label
                          }
                        </td>
                        <td className="text-center">{report.total_boxes}</td>
                        <td className="text-center">{report.active_boxes}</td>
                        <td className="text-center">
                          {report.disconnected_boxes}
                        </td>
                        <td className="text-right">
                          ₹ {report.total_collection.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default DashboardMonthlyReport;
